import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import { useAnnouncements } from '../queries';

/**
 * Dashboard teaser for the newest announcement the caller may read. The feed's
 * own ordering (`-is_pinned, -published_at`) means the first row is either the
 * pinned notice or the newest one — both are the right thing to surface.
 *
 * The body is deliberately NOT rendered here: it is sanitized HTML meant for
 * the feed's prose styling, and a teaser card is the wrong place for headings
 * and lists.
 */
export function LatestAnnouncementCard() {
  const { t, i18n } = useTranslation(['announcements']);
  const announcements = useAnnouncements({ page_size: 1 });
  const latest = announcements.data?.results?.[0];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('announcements:latestCard.title')}</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/announcements">{t('announcements:latestCard.seeAll')}</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {announcements.isLoading ? <Skeleton className="h-12 w-full" /> : null}
        {!announcements.isLoading && !latest ? (
          <p className="text-muted-foreground text-sm">{t('announcements:latestCard.empty')}</p>
        ) : null}
        {latest ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {latest.is_pinned ? (
                <Badge variant="default">{t('announcements:pinned')}</Badge>
              ) : null}
              <Badge variant="secondary">
                {latest.audience === 'school'
                  ? t('announcements:audience.school')
                  : (latest.school_class_name ?? t('announcements:audience.class'))}
              </Badge>
            </div>
            <p className="text-sm font-medium">{latest.title}</p>
            <p className="text-muted-foreground text-xs">
              {t('announcements:card.by', {
                name: `${latest.author.first_name} ${latest.author.last_name}`,
              })}
              {' · '}
              <time
                dateTime={latest.published_at ?? latest.created}
                title={formatDateTime(latest.published_at ?? latest.created)}
              >
                {formatRelativeTime(latest.published_at ?? latest.created, i18n.language)}
              </time>
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
